import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  Skeleton,
  Divider,
  Alert,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  Grid as MuiGrid,
  Card,
  CardContent,
  Pagination,
  Breadcrumbs,
  Link,
  Drawer
} from '@mui/material';
import { productsApi, categoriesApi, ProductFilterOptions } from '../api/api';
import ProductCard from '../components/ProductCard';
import ProductFilters from '../components/ProductFilters';
import FeaturedCollections, { FEATURED_CATEGORIES } from '../components/FeaturedCollections';
import { Link as RouterLink } from 'react-router-dom';
import { useContext } from 'react';
import { StoreContext } from '../App';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import TuneIcon from '@mui/icons-material/Tune';
import CloseIcon from '@mui/icons-material/Close';
import { Product, Category } from '../types/api';
import CategoryIconComponent from '../components/CategoryIcon';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useDebounce } from 'use-debounce';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';

// Helper function to get category image path
const getCategoryImagePath = (categoryName: string): string | null => {
  const name = categoryName.toLowerCase();
  
  // Map category names to image filenames
  if (name.includes('beverage') || name.includes('drink')) {
    return '/images/categories/beverage.webp';
  } else if (name.includes('snack')) {
    return '/images/categories/snacks.webp';
  } else if (name.includes('bulk')) {
    return '/images/categories/bulk.webp';
  } else if (name.includes('produce') || name.includes('fruit') || name.includes('vegetable')) {
    return '/images/categories/produce.webp';
  } else if (name.includes('cereal') || name.includes('grain')) {
    return '/images/categories/cereal.webp';
  } else if (name.includes('cleaning') || name.includes('supplies')) {
    return '/images/categories/cleaning.webp';
  } else if (name.includes('dairy') || name.includes('milk') || name.includes('cheese')) {
    return '/images/categories/dairy.webp';
  } else if (name.includes('allergen') || name.includes('specialty')) {
    return '/images/categories/allergen.webp';
  }
  
  return null;
};

// Define Grid component with proper React memo to prevent hooks issues
const Grid = React.memo(function Grid(props: any) {
  return <MuiGrid {...props} />;
});

// Component to display category image with fallback to icon
const CategoryImageDisplay: React.FC<{ category: Category }> = ({ category }) => {
  const [imageError, setImageError] = React.useState(false);
  const imagePath = getCategoryImagePath(category.name);
  
  React.useEffect(() => {
    setImageError(false);
  }, [category.id]);
  
  return (
    <Box 
      sx={{ 
        mb: 1.5,
        width: '100%',
        aspectRatio: '1 / 1',
        maxWidth: { xs: 80, sm: 96, md: 112 },
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto'
      }}
    >
      {imagePath && !imageError ? (
        <img
          src={imagePath}
          alt={category.name}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            display: 'block'
          }}
          onError={() => setImageError(true)}
        />
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
          <CategoryIconComponent category={category} size={44} />
        </Box>
      )}
    </Box>
  );
};

const HomePage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: categoryId } = useParams<{ id: string }>();
  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('q');
  const storeContext = useContext(StoreContext);
  
  // Product filter state using the ProductFilterOptions interface
  const [filters, setFilters] = useState<ProductFilterOptions>({
    // Removed inStock: true to show out-of-stock items with visual indicators
    limit: 100,
    offset: 0
  });
  
  // Current page for pagination
  const [page, setPage] = useState(1);
  
  // Filter drawer state
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  
  // Toggle filter drawer
  const toggleFilterDrawer = () => {
    setFilterDrawerOpen(!filterDrawerOpen);
  };
  
  // Update filters when category or search changes
  React.useEffect(() => {
    setFilters(prev => ({
      ...prev,
      category: categoryId ? parseInt(categoryId) : undefined,
      keyword: searchQuery || undefined,
      offset: 0
    }));
    setPage(1);
  }, [categoryId, searchQuery]);
  
  // Fetch products using the filters
  const { data: productsData, isLoading: isLoadingProducts } = useQuery({
    queryKey: ['products', filters],
    queryFn: () => {
      return productsApi.getProducts(filters);
    },
  });

  // Fetch categories
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['categories'],
    queryFn: () => categoriesApi.getCategories(),
    staleTime: 300000, // 5 minutes cache
    gcTime: 3600000, // 1 hour (previously cacheTime)
  });
  
  // Check if the category ID is in regular categories
  const categoryInRegularList = categoriesData?.items.some(
    c => c.id.toString() === categoryId
  );
  
  // Check if it's a featured category
  const isFeaturedCategory = categoryId && FEATURED_CATEGORIES.some(
    c => c.id.toString() === categoryId
  );
  
  // Fetch specific category data if needed
  const { data: specificCategoryData } = useQuery({
    queryKey: ['category-direct', categoryId],
    queryFn: () => categoriesApi.getCategory(parseInt(categoryId || '0')),
    staleTime: 300000, // 5 minutes
    // Only run this query if we have a category ID that's not in regular list
    enabled: !!categoryId && !categoryInRegularList && !isLoadingCategories,
  });

  // Products from query results
  const products = React.useMemo(() => {
    if (!productsData) return [];
    return productsData.items;
  }, [productsData]);

  // Get active category name
  const activeCategoryName = React.useMemo(() => {
    if (!categoryId) return null;
    
    // First try to find in the regular categories list
    if (categoriesData) {
      const category = categoriesData.items.find(c => c.id.toString() === categoryId);
      if (category) return category.name;
    }
    
    // If not found in regular categories, check if it's one of our featured collections
    const featuredCategory = FEATURED_CATEGORIES.find(c => c.id.toString() === categoryId);
    if (featuredCategory) return featuredCategory.name;
    
    // If still not found, use the specific category data
    if (specificCategoryData) {
      return specificCategoryData.name;
    }
    
    return null;
  }, [categoryId, categoriesData, specificCategoryData]);

  // Handle filter changes
  const handleFilterChange = (newFilters: ProductFilterOptions) => {
    // Reset to page 1 when filters change
    if (JSON.stringify(newFilters) !== JSON.stringify(filters)) {
      setPage(1);
      newFilters.offset = 0;
    }
    setFilters(newFilters);
  };

  // Handle pagination change
  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
    setFilters(prev => ({
      ...prev,
      offset: (value - 1) * (prev.limit || 12)
    }));
    // Scroll to top when page changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Calculate total number of pages
  const totalItems = productsData?.total || 0;
  const itemsPerPage = filters.limit || 12;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Breadcrumbs
  const breadcrumbs = [
    <Link component={RouterLink} to="/" color="inherit" key="home" underline="hover">
      Home
    </Link>
  ];
  
  if (activeCategoryName) {
    breadcrumbs.push(
      <Typography color="text.primary" key="category">
        {activeCategoryName}
      </Typography>
    );
  }
  
  if (searchQuery) {
    breadcrumbs.push(
      <Typography color="text.primary" key="search">
        Search: {searchQuery}
      </Typography>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {(categoryId || searchQuery) && (
        <Paper 
          elevation={0} 
          sx={{ 
            p: 2, 
            mb: 3, 
            borderRadius: 2, 
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Breadcrumbs 
            separator={<NavigateNextIcon fontSize="small" />} 
            aria-label="breadcrumb"
          >
            {breadcrumbs}
          </Breadcrumbs>
        </Paper>
      )}
      
      {/* Title based on page type */}
      {searchQuery && !categoryId && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Search Results: "{searchQuery}"
          </Typography>
          <Divider sx={{ mb: 2 }} />
        </Box>
      )}

      {categoryId && activeCategoryName && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            {activeCategoryName}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          {/* Display subcategories if present */}
          {categoriesData?.items.some(cat => cat.parentId === Number(categoryId)) && (
            <Box sx={{ mt: 2, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Subcategories:
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {categoriesData?.items
                  .filter(cat => cat.parentId === Number(categoryId))
                  .map(subCategory => (
                    <Chip
                      key={subCategory.id}
                      label={subCategory.name}
                      component={RouterLink}
                      to={`/category/${subCategory.id}`}
                      clickable
                      size="medium"
                      color="primary"
                      variant="outlined"
                      sx={{
                        '&:hover': {
                          opacity: 0.9,
                          background: 'linear-gradient(45deg, #008000, #008000)',
                          color: 'white',
                          transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out'
                        }
                      }}
                    />
                  ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Categories Section - Only show on homepage */}
      {!searchQuery && !categoryId && (
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" component="h5" gutterBottom fontWeight="bold">
            Shop by Category
          </Typography>
          <Divider sx={{ mb: 4 }} />
          
          {isLoadingCategories ? (
            // Skeleton loading state
            <Box sx={{ 
              display: 'grid', 
              gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3
            }}>
              {Array.from(new Array(6)).map((_, index) => (
                <Box key={index}>
                  <Skeleton variant="rounded" height={180} />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" width="60%" />
                </Box>
              ))}
            </Box>
          ) : (
            // Group categories by parent ID
            <Box>
              {/* Main categories (parent categories) */}
              <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(4, 1fr)', lg: 'repeat(8, 1fr)' },
                gap: { xs: 1.5, md: 2 },
                mb: 4
              }}>
                {categoriesData?.items
                  .filter(category => !category.parentId && ![177597826, 142312356].includes(category.id))
                  .map(category => (
                    <Paper
                      key={category.id}
                      elevation={0}
                      component={RouterLink}
                      to={`/category/${category.id}`}
                      sx={{
                        p: { xs: 2, md: 2.5 },
                        borderRadius: 2,
                        textAlign: 'center',
                        border: '1px solid #f0f0f0',
                        textDecoration: 'none',
                        color: 'inherit',
                        transition: 'all 0.2s ease-in-out',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: { xs: 120, sm: 130, md: 140 },
                        backgroundColor: 'background.paper',
                        overflow: 'hidden',
                        position: 'relative',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                          transform: 'translateY(-2px)',
                          borderColor: '#2E7D32',
                          backgroundColor: '#fafafa'
                        }
                      }}
                    >
                      <CategoryImageDisplay category={category} />
                      <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: 600,
                            color: 'text.primary',
                            fontSize: { xs: '0.8rem', sm: '0.875rem', md: '0.95rem' },
                            lineHeight: 1.3,
                            mb: 0.25
                          }}
                        >
                          {category.name}
                        </Typography>
                      </Box>
                    </Paper>
                  ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Featured Collections - Only show on homepage */}
      {!searchQuery && !categoryId && (
        <FeaturedCollections />
      )}

      {/* Product listing section */}
      {(searchQuery || categoryId || !isLoadingCategories) && (
        <Box sx={{ mt: searchQuery || categoryId ? 0 : 6 }}>
          {!searchQuery && !categoryId && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" component="h5" gutterBottom fontWeight="bold">
                All Products
              </Typography>
              <Divider sx={{ mb: 2 }} />
            </Box>
          )}
          
          {/* Filter button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={toggleFilterDrawer}
              sx={{ borderRadius: 2 }}
            >
              Filters
            </Button>
          </Box>
          
          {/* Filter drawer */}
          <Drawer
            anchor="right"
            open={filterDrawerOpen}
            onClose={toggleFilterDrawer}
            sx={{
              '& .MuiDrawer-paper': {
                width: { xs: '100%', sm: 400 },
                p: 3,
                boxSizing: 'border-box',
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Filters</Typography>
              <IconButton onClick={toggleFilterDrawer}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ mb: 2 }} />
            <ProductFilters 
              filters={filters}
              onFilterChange={(newFilters) => {
                handleFilterChange(newFilters);
                // Don't close drawer on filter change to allow multiple selections
              }}
              categories={categoriesData?.items}
              currentCategoryId={categoryId ? parseInt(categoryId) : undefined}
              isLoading={isLoadingProducts}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained"
                onClick={toggleFilterDrawer}
                sx={{ mt: 2 }}
              >
                Apply Filters
              </Button>
            </Box>
          </Drawer>
          
          {/* Product grid */}
          {isLoadingProducts ? (
            // Product loading skeleton
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: { 
                xs: 'repeat(2, 1fr)',  // 2 items on mobile 
                sm: 'repeat(2, 1fr)',  // 2 items on small tablets
                md: 'repeat(3, 1fr)',  // 3 items on tablets/small laptops
                lg: 'repeat(4, 1fr)',  // 4 items on laptops/desktops
                xl: 'repeat(5, 1fr)'   // 5 items on large screens
              },
              gap: 3
            }}>
              {Array.from(new Array(10)).map((_, index) => (
                <Box key={index} sx={{ width: '100%' }}>
                  <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, width: '100%' }} />
                  <Skeleton variant="text" sx={{ mt: 1 }} />
                  <Skeleton variant="text" width="60%" />
                  <Skeleton variant="text" width="40%" />
                </Box>
              ))}
            </Box>
          ) : products.length > 0 ? (
            <>
              {/* Results summary */}
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Showing {Math.min(filters.limit || 12, products.length)} of {productsData?.total || 0} products
                </Typography>
              </Box>
              
              {/* Product grid */}
              <Box sx={{ 
                display: 'grid',
                gridTemplateColumns: { 
                  xs: 'repeat(2, 1fr)',  // 2 items on mobile 
                  sm: 'repeat(2, 1fr)',  // 2 items on small tablets
                  md: 'repeat(3, 1fr)',  // 3 items on tablets/small laptops
                  lg: 'repeat(4, 1fr)',  // 4 items on laptops/desktops
                  xl: 'repeat(5, 1fr)'   // 5 items on large screens
                },
                gap: 3
              }}>
                {products.map(product => (
                  <Box key={product.id} sx={{ width: '100%' }}>
                    <ProductCard product={product} />
                  </Box>
                ))}
              </Box>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                  <Pagination 
                    count={totalPages} 
                    page={page} 
                    onChange={handlePageChange} 
                    color="primary"
                  />
                </Box>
              )}
            </>
          ) : (
            // No results
            <Alert 
              severity="info" 
              sx={{ 
                borderRadius: 2, 
                py: 2 
              }}
            >
              No products found. Try adjusting your filters.
            </Alert>
          )}
        </Box>
      )}
    </Container>
  );
};

export default HomePage; 